class ProductArtworkUpload extends HTMLElement {
  #controller = new AbortController();
  /** @type {string | null} */
  #objectUrl = null;
  /** @type {HTMLElement[]} */
  #gatedSiblings = [];

  connectedCallback() {
    const { signal } = this.#controller;
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[ref="input"]'));
    const dropzone = /** @type {HTMLElement | null} */ (this.querySelector('[ref="dropzone"]'));
    const preview = /** @type {HTMLElement | null} */ (this.querySelector('[ref="preview"]'));
    const thumb = /** @type {HTMLImageElement | null} */ (this.querySelector('[ref="thumb"]'));
    const fileIcon = /** @type {HTMLElement | null} */ (this.querySelector('[ref="fileIcon"]'));
    const filename = /** @type {HTMLElement | null} */ (this.querySelector('[ref="filename"]'));
    const remove = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="remove"]'));
    const errorEl = /** @type {HTMLElement | null} */ (this.querySelector('[ref="error"]'));
    if (!input || !dropzone || !preview || !thumb || !fileIcon || !filename || !remove || !errorEl) return;

    const maxSizeMb = Number(this.dataset.maxSizeMb || 20);
    const maxBytes = maxSizeMb * 1024 * 1024;
    const gatedCount = Number(this.dataset.gatedBlockCount || 0);

    this.#collectGatedSiblings(gatedCount);
    this.#applyGate(false);

    const showError = (msg) => {
      errorEl.textContent = msg;
      errorEl.hidden = !msg;
    };

    const releaseObjectUrl = () => {
      if (this.#objectUrl) {
        URL.revokeObjectURL(this.#objectUrl);
        this.#objectUrl = null;
      }
    };

    const isImage = (file) => {
      if (file.type && file.type.startsWith('image/')) return true;
      return /\.(png|jpe?g|gif|webp|svg)$/i.test(file.name);
    };

    const updatePreview = () => {
      const file = input.files && input.files[0];
      releaseObjectUrl();
      if (!file) {
        preview.hidden = true;
        thumb.hidden = true;
        thumb.removeAttribute('src');
        fileIcon.hidden = true;
        filename.textContent = '';
        dropzone.removeAttribute('data-has-file');
        this.#applyGate(false);
        this.#dispatchChange(false);
        return;
      }
      if (file.size > maxBytes) {
        showError(`File exceeds maximum size of ${maxSizeMb}MB.`);
        input.value = '';
        preview.hidden = true;
        thumb.hidden = true;
        fileIcon.hidden = true;
        dropzone.removeAttribute('data-has-file');
        this.#applyGate(false);
        this.#dispatchChange(false);
        return;
      }
      showError('');
      filename.textContent = file.name;
      if (isImage(file)) {
        this.#objectUrl = URL.createObjectURL(file);
        thumb.src = this.#objectUrl;
        thumb.hidden = false;
        fileIcon.hidden = true;
      } else {
        thumb.hidden = true;
        thumb.removeAttribute('src');
        fileIcon.hidden = false;
      }
      preview.hidden = false;
      dropzone.setAttribute('data-has-file', 'true');
      this.#applyGate(true);
      this.#dispatchChange(true);
    };

    input.addEventListener('change', updatePreview, { signal });

    remove.addEventListener('click', (e) => {
      e.preventDefault();
      input.value = '';
      updatePreview();
    }, { signal });

    ['dragenter', 'dragover'].forEach((evt) => {
      dropzone.addEventListener(evt, (e) => {
        e.preventDefault();
        dropzone.setAttribute('data-drag-over', 'true');
      }, { signal });
    });
    ['dragleave', 'drop'].forEach((evt) => {
      dropzone.addEventListener(evt, (e) => {
        e.preventDefault();
        dropzone.removeAttribute('data-drag-over');
      }, { signal });
    });
    dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      const dt = /** @type {DragEvent} */ (e).dataTransfer;
      if (dt && dt.files && dt.files.length) {
        input.files = dt.files;
        updatePreview();
      }
    }, { signal });
  }

  /**
   * Finds the N following sibling block roots to hide until a file is uploaded.
   * Inside _product-details, child blocks render as direct siblings of this
   * component (no per-block wrapper), so we walk this.nextElementSibling.
   * @param {number} count
   */
  #collectGatedSiblings(count) {
    if (count <= 0) return;
    let next = this.nextElementSibling;
    while (next && this.#gatedSiblings.length < count) {
      if (next instanceof HTMLElement) {
        this.#gatedSiblings.push(next);
      }
      next = next.nextElementSibling;
    }
  }

  /** @param {boolean} hasFile */
  #applyGate(hasFile) {
    for (const el of this.#gatedSiblings) {
      el.classList.toggle('artwork-upload-gated', !hasFile);
    }
    this.#toggleAddToCart(hasFile);
  }

  /**
   * Disables the add-to-cart submit button in the linked product form until
   * a file is uploaded. Re-enables once a file is present.
   * @param {boolean} hasFile
   */
  #toggleAddToCart(hasFile) {
    const formId = this.dataset.formId;
    if (!formId) return;
    const form = document.getElementById(formId);
    if (!form) return;
    const buttons = form.querySelectorAll('button[name="add"]');
    buttons.forEach((btn) => {
      if (!(btn instanceof HTMLButtonElement)) return;
      if (hasFile) {
        if (btn.dataset.artworkGated === 'true') {
          btn.disabled = false;
          delete btn.dataset.artworkGated;
        }
      } else if (!btn.disabled) {
        btn.disabled = true;
        btn.dataset.artworkGated = 'true';
      }
    });
  }

  /** @param {boolean} hasFile */
  #dispatchChange(hasFile) {
    this.dispatchEvent(new CustomEvent('artwork-upload:change', {
      bubbles: true,
      detail: {
        hasFile,
        formId: this.dataset.formId || null,
        propertyLabel: this.dataset.propertyLabel || null,
      },
    }));
  }

  disconnectedCallback() {
    if (this.#objectUrl) {
      URL.revokeObjectURL(this.#objectUrl);
      this.#objectUrl = null;
    }
    this.#controller.abort();
  }
}

if (!customElements.get('product-artwork-upload')) {
  customElements.define('product-artwork-upload', ProductArtworkUpload);
}
